import type { ComponentType } from 'react';

import { AdminSetupArticle } from './articles/admin-setup';
import { FaqArticle } from './articles/faq';
import { GlossaryArticle } from './articles/glossary';
import { LoggingTimeArticle } from './articles/logging-time';
import { McpSetupArticle } from './articles/mcp-setup';
import { OverviewArticle } from './articles/overview';
import { PlansAndSeatsArticle } from './articles/plans-and-seats';
import { ReadingReportsArticle } from './articles/reading-reports';

/**
 * ヘルプ記事のレジストリ。
 *
 * 索引・記事ナビ・絞り込みの 3 つが同じ配列を読む。記事を足すときは
 * ここに 1 行と本文の TSX を 1 枚。順序は表示順そのもの。
 *
 * ヘルプは公開ページなので、テナント固有の値 (実エンドポイント・PAT) は
 * 出さない。実物が要る操作は画面へのリンクで送る。
 */

/** 誰に向けた記事か */
export type HelpAudience = 'common' | 'engineer' | 'admin';

export interface HelpArticle {
  slug: string;
  title: string;
  /** 索引に出す 1〜2 文 */
  summary: string;
  audience: HelpAudience;
  /** 絞り込みでだけ使う語。本文には出さない表記ゆれの受け皿 */
  keywords: string[];
  Component: ComponentType;
}

export const HELP_ARTICLES: readonly HelpArticle[] = [
  {
    slug: 'overview',
    title: 'INOU の考え方',
    summary:
      '何をどの粒度で管理するのか、どこから記録するのか。他の記事はすべてこの前提の上に立っています。',
    audience: 'common',
    keywords: ['はじめに', '概要', '粒度', 'ロール', 'overview'],
    Component: OverviewArticle,
  },
  {
    slug: 'mcp-setup',
    title: 'MCP をつなぐ',
    summary:
      'トークンを発行して AI クライアントに登録するまで。Claude Code / Codex / Copilot / Cursor / Claude Desktop の設定を載せています。',
    audience: 'engineer',
    keywords: [
      'MCP',
      'トークン',
      'PAT',
      '接続',
      'Claude Code',
      'Codex',
      'Copilot',
      'Cursor',
      'Claude Desktop',
      'setup',
    ],
    Component: McpSetupArticle,
  },
  {
    slug: 'logging-time',
    title: '工数の記録',
    summary:
      'どう頼めば記録されるか、記録先がどう決まるか、そして作業カテゴリと資産化を聞かれない理由。',
    audience: 'engineer',
    keywords: ['記録', '工数', 'カテゴリ', 'リポジトリ', '訂正', 'log_time'],
    Component: LoggingTimeArticle,
  },
  {
    slug: 'admin-setup',
    title: '初期設定の順番',
    summary: 'プロジェクト種別からアサイン計画まで、後ろが前を参照する順に並べた立ち上げの手順。',
    audience: 'admin',
    keywords: ['初期設定', 'プロジェクト種別', '招待', '単価', 'アサイン', 'タイムゾーン'],
    Component: AdminSetupArticle,
  },
  {
    slug: 'reading-reports',
    title: 'レポートの読み方',
    summary:
      '直接と間接の数え方、資産化される条件、単価の決まり方。過去の月の数字が動く条件もここにあります。',
    audience: 'admin',
    keywords: ['レポート', '稼働', '資産化', '単価', '直接', '間接', '収支'],
    Component: ReadingReportsArticle,
  },
  {
    slug: 'plans-and-seats',
    title: 'プランとシート',
    summary: 'ロールとプランの対応、シートを消費するタイミング、トライアルが終わったあとの挙動。',
    audience: 'admin',
    keywords: ['プラン', 'シート', '料金', 'トライアル', '課金', 'ロール'],
    Component: PlansAndSeatsArticle,
  },
  {
    slug: 'faq',
    title: 'よくある質問',
    summary: '短く答えられるものをまとめています。詳しい説明は各記事に返します。',
    audience: 'common',
    keywords: ['FAQ', 'よくある質問', '質問'],
    Component: FaqArticle,
  },
  {
    slug: 'glossary',
    title: '用語集',
    summary: '画面と記事に出てくる言葉の意味。引くためのものなので説明は短めです。',
    audience: 'common',
    keywords: ['用語', '用語集', '意味', 'glossary'],
    Component: GlossaryArticle,
  },
];

/** 索引と記事ナビの並び。読み手ごとに固まりを作る */
export const HELP_GROUPS: ReadonlyArray<{
  audience: HelpAudience;
  label: string;
  /** 英字ラベル。デザイン上ここは必ず等幅 */
  eyebrow: string;
}> = [
  { audience: 'common', label: 'はじめに', eyebrow: 'BASICS' },
  { audience: 'engineer', label: 'エンジニア向け', eyebrow: 'ENGINEER' },
  { audience: 'admin', label: '管理者向け', eyebrow: 'ADMIN' },
];

export function findHelpArticle(slug: string | undefined): HelpArticle | null {
  if (!slug) return null;
  return HELP_ARTICLES.find((article) => article.slug === slug) ?? null;
}

export function helpArticlesOf(audience: HelpAudience): HelpArticle[] {
  return HELP_ARTICLES.filter((article) => article.audience === audience);
}
